import React from 'react';
import {
  LuChevronDown, LuClipboardList, LuDownload, LuFileText, LuGavel, LuMail, LuMessageCircle, LuPencil,
} from 'react-icons/lu';
import { PageLayout } from '../../../app/PageLayout';
import { Badge, Button } from '../../../components/ui';
import { formatDate, plural, t } from '../../../i18n';
import { ptBR } from '../../../i18n/pt-BR';
import { Surgery } from '../../../types/patient.types';
import { Order } from '../types';
import { StatusBadge } from '../StatusBadge';
import { cidsOf, countProcedures } from '../utils';

export type GuideKind = 'guia' | 'opme';

interface OrderDetailScreenProps {
  order: Order;
  surgery: Surgery;
  onBack: () => void;
  onEditOrder: () => void;
  onGuide: (kind: GuideKind) => void;
  onDownloadResumo: () => void;
  onDownloadRecurso: () => void;
  onCreateAppeal: () => void;
  onOpenSendEmail: () => void;
  onOpenSendWhatsApp: () => void;
  loadingGuia?: boolean;
  loadingOpme?: boolean;
  loadingResumo?: boolean;
  loadingRecurso?: boolean;
}

const Section: React.FC<{ icon: React.ReactNode; title: string; open?: boolean; children: React.ReactNode }> = ({
  icon, title, open, children,
}) => (
  <details open={open} className="group overflow-hidden rounded-card border border-line bg-surface shadow-raised">
    <summary className="flex min-h-header cursor-pointer list-none items-center gap-3 px-4 py-3.5 font-semibold [&::-webkit-details-marker]:hidden">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-control bg-primary-soft text-primary" aria-hidden>{icon}</span>
      {title}
      <LuChevronDown size={20} aria-hidden className="ml-auto text-primary transition-transform duration-base ease-out group-open:rotate-180" />
    </summary>
    <div className="flex animate-rise flex-col gap-2.5 px-4 pb-4">{children}</div>
  </details>
);

const ShareButton: React.FC<{ icon: React.ReactNode; tone: string; label: string; onClick: () => void }> = ({ icon, tone, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex min-h-16 items-center gap-3 rounded-card border border-line bg-surface p-3.5 text-left font-semibold shadow-raised transition-all duration-fast hover:-translate-y-px hover:border-primary focus-visible:outline-none focus-visible:shadow-focus"
  >
    <span className={`grid h-card-icon w-card-icon shrink-0 place-items-center rounded-full ${tone}`} aria-hidden>{icon}</span>
    {label}
  </button>
);

export const OrderDetailScreen: React.FC<OrderDetailScreenProps> = ({
  order,
  surgery,
  onBack,
  onEditOrder,
  onGuide,
  onDownloadResumo,
  onDownloadRecurso,
  onCreateAppeal,
  onOpenSendEmail,
  onOpenSendWhatsApp,
  loadingGuia,
  loadingOpme,
  loadingResumo,
  loadingRecurso,
}) => {
  const summary: [string, string][] = [
    [t('savedOrders.summary.date'), formatDate(surgery.timestamp)],
    [t('savedOrders.summary.hospital'), surgery.hospital || '—'],
    [t('savedOrders.summary.procedures'), plural(countProcedures(surgery), ptBR.savedOrders.summary.codes)],
    [t('savedOrders.summary.opme'), plural(surgery.opme_list?.length ?? 0, ptBR.savedOrders.summary.items)],
    [t('savedOrders.summary.cid'), cidsOf(surgery.pedido_cirurgia) || '—'],
  ];

  return (
    <PageLayout
      title={order.name || t('savedOrders.noProcedure')}
      subtitle={surgery.pacient_name}
      onBack={onBack}
      action={<span className="hidden sm:inline-flex"><StatusBadge status={order.status} /></span>}
    >
      <span className="-mt-1 sm:hidden"><StatusBadge status={order.status} /></span>

      <div className="stagger flex flex-col gap-4">
        <Section icon={<LuClipboardList size={20} />} title={t('savedOrders.summary.title')} open>
          <dl className="grid grid-cols-kv gap-x-3.5 gap-y-1.5 text-label">
            {summary.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt className="text-fg-muted">{label}</dt>
                <dd className="whitespace-pre-line break-words font-medium">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
          <Button variant="outline" size="sm" className="self-start" icon={<LuDownload aria-hidden />} loading={loadingResumo} onClick={onDownloadResumo}>
            {t('savedOrders.summary.download')}
          </Button>
        </Section>

        <Section icon={<LuFileText size={20} />} title={t('savedOrders.guide.title')}>
          <p className="text-fg-muted">{t('savedOrders.guide.hint')}</p>
          <div className="grid grid-cols-2 gap-2.5">
            <Button variant="outline" icon={<LuDownload aria-hidden />} loading={loadingGuia} onClick={() => onGuide('guia')}>
              {t('savedOrders.guide.admission')}
            </Button>
            <Button variant="outline" icon={<LuDownload aria-hidden />} loading={loadingOpme} onClick={() => onGuide('opme')}>
              {t('savedOrders.guide.opme')}
            </Button>
          </div>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <ShareButton icon={<LuMail size={20} />} tone="bg-primary-soft text-primary" label={t('savedOrders.share.email')} onClick={onOpenSendEmail} />
            <ShareButton icon={<LuMessageCircle size={20} />} tone="bg-whatsapp-soft text-whatsapp" label={t('savedOrders.share.whatsapp')} onClick={onOpenSendWhatsApp} />
          </div>
        </Section>

        <Section icon={<LuPencil size={20} />} title={t('savedOrders.edit.title')}>
          <p className="text-fg-muted">{t('savedOrders.edit.hint')}</p>
          <Button variant="primary" size="sm" className="self-start" onClick={onEditOrder}>
            {t('savedOrders.edit.action')}
          </Button>
        </Section>

        <Section icon={<LuGavel size={20} />} title={t('savedOrders.appeal.title')} open={order.hasRecurso}>
          {order.hasRecurso ? (
            <>
              <span className="self-start"><Badge tone="success">{t('savedOrders.appeal.done')}</Badge></span>
              <Button variant="outline" size="sm" className="self-start" icon={<LuDownload aria-hidden />} loading={loadingRecurso} onClick={onDownloadRecurso}>
                {t('savedOrders.appeal.download')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-fg-muted">{t('savedOrders.appeal.none')}</p>
              <Button variant="primary" size="sm" className="self-start" onClick={onCreateAppeal}>
                {t('savedOrders.appeal.create')}
              </Button>
            </>
          )}
        </Section>
      </div>
    </PageLayout>
  );
};
